import { ArrowDown, ArrowUp } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { truncate } from "./chart-utils";

interface ProfileComparisonProps {
  campaignId: string;
  profileId: string | null;
}

interface ScoringProfile {
  id: string;
  name: string;
}

interface ComparisonEntry {
  id: string;
  addressRaw: string;
  city: string | null;
  scores: Record<string, number>;
}

// Brand chart family (yellow → mint → amber → sapphire → coral), matching the
// --chart-* tokens, so multiple profiles read as distinct but on-brand.
const PROFILE_COLORS: Record<string, string> = {
  Universal: "#f5c344",
  Family: "#2e9f70",
  "Young Pro": "#c97d2d",
  Investor: "#2a4a82",
};

const FALLBACK_COLORS = ["#f5c344", "#2e9f70", "#c97d2d", "#2a4a82", "#c75c5c", "#5b7fc4"];

function profileColor(name: string, index: number): string {
  return PROFILE_COLORS[name] ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length] ?? "#f5c344";
}

type SortKey = "address" | "current" | "delta" | string;

function SortHeader({
  label,
  sKey,
  sortKey,
  sortDesc,
  onSort,
}: {
  label: string;
  sKey: SortKey;
  sortKey: SortKey;
  sortDesc: boolean;
  onSort: (key: SortKey) => void;
}) {
  return (
    <th
      className="px-2 py-1.5 text-xs text-muted-foreground font-medium cursor-pointer hover:text-foreground text-right"
      onClick={() => onSort(sKey)}
    >
      {label}
      {sortKey === sKey && <span className="ml-0.5">{sortDesc ? "↓" : "↑"}</span>}
    </th>
  );
}

export function ProfileComparison({ campaignId, profileId }: ProfileComparisonProps) {
  const [profiles, setProfiles] = useState<ScoringProfile[]>([]);
  const [entries, setEntries] = useState<ComparisonEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("current");
  const [sortDesc, setSortDesc] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const profilesRes = await fetch("/api/asset-profiles");
      if (!profilesRes.ok) throw new Error("Failed to fetch profiles");
      const profilesJson = await profilesRes.json();
      const allProfiles: ScoringProfile[] = profilesJson.data ?? [];
      setProfiles(allProfiles);

      if (allProfiles.length === 0) {
        setEntries([]);
        setLoading(false);
        return;
      }

      const presetIds = allProfiles.map((p) => p.id).join(",");
      const compRes = await fetch(
        `/api/campaigns/${campaignId}/comparison?profiles=${presetIds}&limit=20`,
      );
      if (!compRes.ok) throw new Error("Failed to fetch comparison data");
      const compJson = await compRes.json();
      setEntries(compJson.locations ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load comparison");
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSort = useCallback((key: SortKey) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDesc((d) => !d);
        return key;
      }
      setSortDesc(true);
      return key;
    });
  }, []);

  const altProfiles = useMemo(
    () => profiles.filter((p) => p.id !== profileId),
    [profiles, profileId],
  );

  const enriched = useMemo(
    () =>
      !profileId
        ? []
        : entries.map((entry) => {
            const currentScore = entry.scores[profileId] ?? 0;
            let bestAltScore = 0;
            for (const p of profiles) {
              if (p.id === profileId) continue;
              const s = entry.scores[p.id] ?? 0;
              if (s > bestAltScore) bestAltScore = s;
            }
            const delta = bestAltScore - currentScore;
            return { ...entry, currentScore, delta };
          }),
    [entries, profiles, profileId],
  );

  const sorted = useMemo(
    () =>
      [...enriched].sort((a, b) => {
        let cmp = 0;
        if (sortKey === "address") cmp = a.addressRaw.localeCompare(b.addressRaw);
        else if (sortKey === "current") cmp = a.currentScore - b.currentScore;
        else if (sortKey === "delta") cmp = a.delta - b.delta;
        else cmp = (a.scores[sortKey] ?? 0) - (b.scores[sortKey] ?? 0);
        return sortDesc ? -cmp : cmp;
      }),
    [enriched, sortKey, sortDesc],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-muted-foreground">Loading comparison...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }

  if (entries.length === 0 || !profileId) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center">
        No comparison data available. Score locations with multiple profiles to see a comparison.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      {/* Legend */}
      <div className="flex flex-wrap gap-4 mb-3">
        {profiles.map((p, i) => (
          <div key={p.id} className="flex items-center gap-1.5">
            <div
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: profileColor(p.name, i) }}
            />
            <span className="text-xs text-muted-foreground">{p.name}</span>
          </div>
        ))}
      </div>

      <table className="w-full min-w-[500px] text-sm">
        <thead>
          <tr>
            <th
              className="text-left px-2 py-1.5 text-xs text-muted-foreground font-medium cursor-pointer hover:text-foreground"
              onClick={() => handleSort("address")}
            >
              Location
              {sortKey === "address" && <span className="ml-0.5">{sortDesc ? "↓" : "↑"}</span>}
            </th>
            <SortHeader
              label="Current"
              sKey="current"
              sortKey={sortKey}
              sortDesc={sortDesc}
              onSort={handleSort}
            />
            {altProfiles.map((p) => (
              <SortHeader
                key={p.id}
                label={p.name}
                sKey={p.id}
                sortKey={sortKey}
                sortDesc={sortDesc}
                onSort={handleSort}
              />
            ))}
            <SortHeader
              label="Delta"
              sKey="delta"
              sortKey={sortKey}
              sortDesc={sortDesc}
              onSort={handleSort}
            />
          </tr>
        </thead>
        <tbody>
          {sorted.map((entry) => (
            <tr key={entry.id} className="border-t border-border">
              <td className="px-2 py-1.5 truncate max-w-[200px]" title={entry.addressRaw}>
                {truncate(entry.addressRaw, 32)}
              </td>
              <td className="px-2 py-1.5 text-right font-medium tabular-nums">
                {Math.round(entry.currentScore)}
              </td>
              {altProfiles.map((p, i) => {
                const score = entry.scores[p.id] ?? 0;
                return (
                  <td key={p.id} className="px-2 py-1.5 text-right tabular-nums">
                    <span className="inline-flex items-center gap-1">
                      <span
                        className="inline-block w-2 h-2 rounded-full"
                        style={{ backgroundColor: profileColor(p.name, i) }}
                      />
                      {Math.round(score)}
                    </span>
                  </td>
                );
              })}
              <td className="px-2 py-1.5 text-right tabular-nums">
                {entry.delta > 0 ? (
                  <span className="inline-flex items-center gap-0.5 text-score-high">
                    <ArrowUp className="size-3" />+{Math.round(entry.delta)}
                  </span>
                ) : entry.delta < 0 ? (
                  <span className="inline-flex items-center gap-0.5 text-score-low">
                    <ArrowDown className="size-3" />
                    {Math.round(entry.delta)}
                  </span>
                ) : (
                  <span className="text-muted-foreground">0</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
